
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/**
 * Hook to diagnose and fix database configuration issues for vector search
 */
export const useDatabaseDiagnostics = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState<Record<string, any>>({});
  const { toast } = useToast();

  /**
   * Check if a specific function exists in the database
   */
  const checkFunctionExists = async (functionName: string): Promise<boolean> => {
    try {
      // Using a type assertion to bypass the TypeScript error with postgres_inspect
      const { data, error } = await (supabase.rpc as any)('postgres_inspect', {
        input_table: 'pg_proc',
        input_column: 'proname'
      });
      
      if (error) {
        console.error(`Error checking for function ${functionName}:`, error);
        return false;
      }

      // Use type guard to ensure data is an array before using .some()
      return Array.isArray(data) && data.some((row: any) => row.proname === functionName);
    } catch (error) {
      console.error(`Error checking function ${functionName}:`, error);
      return false;
    }
  };

  /**
   * Check if a specific column exists in a table
   */
  const checkColumnExists = async (tableName: string, columnName: string): Promise<boolean> => {
    try {
      // Using a type assertion to bypass the TypeScript error with postgres_inspect
      const { data, error } = await (supabase.rpc as any)('postgres_inspect', {
        input_table: tableName,
        input_column: columnName
      });
      
      if (error) {
        console.error(`Error checking for column ${columnName} in ${tableName}:`, error);
        return false;
      }

      return Array.isArray(data) && data.length > 0;
    } catch (error) {
      console.error(`Error checking column ${columnName} in ${tableName}:`, error);
      return false;
    }
  };

  /**
   * Check if the pgvector extension is installed
   */
  const checkPgVectorExtension = async (): Promise<boolean> => {
    try {
      // Check if vector operators exist as a proxy for extension being installed
      const { data, error } = await (supabase.rpc as any)('postgres_inspect', {
        input_table: 'pg_operator',
        input_column: 'oprname'
      });
      
      if (error) {
        console.error("Error checking for pgvector extension:", error);
        return false;
      }

      // Use type guard to ensure data is an array before using .some()
      return Array.isArray(data) && data.some((row: any) => row.oprname === "<==>"); 
    } catch (error) {
      console.error("Error checking pgvector extension:", error);
      return false;
    }
  };

  /**
   * Run diagnostics on the database setup for vector search
   */
  const runDiagnostics = async () => {
    setIsLoading(true);
    const results: Record<string, any> = {};

    try {
      // Check postgres_inspect function
      results.postgres_inspect = await checkFunctionExists('postgres_inspect');
      
      // Check match functions for vector search
      results.match_candidates = await checkFunctionExists('match_candidates');
      results.match_jobs = await checkFunctionExists('match_jobs');
      results.match_companies = await checkFunctionExists('match_companies');
      
      // Check pgvector extension
      results.pgvector_extension = await checkPgVectorExtension();
      
      // Check embedding columns
      results.candidates_embedding = await checkColumnExists('candidates', 'embedding');
      results.jobs_embedding = await checkColumnExists('jobs', 'embedding');
      results.companies_embedding = await checkColumnExists('companies', 'embedding');
      
      setDiagnosticResults(results);
      
      // Simple summary of issues
      const issues = Object.entries(results)
        .filter(([_, value]) => value === false)
        .map(([key]) => key);

      if (issues.length === 0) {
        toast({
          title: "Database Diagnostics",
          description: "All checks passed successfully!",
        });
      } else {
        toast({
          title: "Database Configuration Issues",
          description: `Found issues with: ${issues.join(", ")}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error running database diagnostics:", error);
      toast({
        title: "Diagnostics Error",
        description: "Failed to complete database diagnostics",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Attempt to fix common database configuration issues automatically
   */
  const fixDatabaseIssues = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.functions.invoke("fix-database-setup", {
        body: { runDiagnostics: true }
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Database Fix Attempted",
        description: "Database configuration fix has been attempted. Please run diagnostics again to verify.",
      });
      
      // Run diagnostics again after fix attempt
      await runDiagnostics();
    } catch (error) {
      console.error("Error fixing database issues:", error);
      toast({
        title: "Fix Error",
        description: "Failed to fix database configuration issues",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    diagnosticResults,
    runDiagnostics,
    fixDatabaseIssues
  };
};
